import { useEffect, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, KeyRound, ArrowRight, Theater, Trash2, Plus, Upload, CheckCircle2, User as UserIcon, Clock, ShieldCheck } from "lucide-react";
import DatePickerField from "@/components/DatePickerField";
import CustomFieldRenderer from "@/components/CustomFieldRenderer";
import TimeInput12 from "@/components/TimeInput12";
import { fmtDate, fmtTime } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });
const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const STAGE_IMG = "https://images.unsplash.com/photo-1613210434051-4b00d62d03fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMHRoZWF0ZXIlMjBzdGFnZSUyMGRhcmslMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMzU0OTI3fDA&ixlib=rb-4.1.0&q=85";

const emptyForm = {
  name: "", email: "", phone: "",
  age: "", dob: "", height: "", vocal_range: "", dance_experience: "",
  parent_name: "", parent_email: "", parent_phone: "", parent_consent: false,
  previous_experience: "", availability_start: "", availability_end: "",
  special_skills: "", emergency_contact_name: "", emergency_contact_phone: "",
  interested_roles: [], conflicts: [], slot_id: "",
  is_virtual: false, video_acting_url: "", video_vocal_url: "", video_dance_url: "",
};

// Return true if the YYYY-MM-DD DOB indicates the person is under 18.
// Blank / malformed DOBs are treated as NOT a minor so the age gate never
// blocks adults who don't want to share a birthday. The backend does the
// same computation and is the source of truth.
const calcIsMinor = (dob) => {
  if (!dob) return false;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 && age < 18;
};

export default function AuditionSignup() {
  const [sp] = useSearchParams();
  const { code: routeCode } = useParams();
  const initialCode = (routeCode || sp.get("code") || "").toUpperCase();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState(initialCode);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [slots, setSlots] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [customFiles, setCustomFiles] = useState({});
  const [honeypot, setHoneypot] = useState("");
  const [roleQuery, setRoleQuery] = useState("");
  const [headshot, setHeadshot] = useState(null);
  const [headshotPreviewUrl, setHeadshotPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const isMinor = calcIsMinor(form.dob);

  useEffect(() => { if (initialCode) fetchPreview(initialCode); }, [initialCode]);  

  const fetchPreview = async (c) => {
    setPreviewErr(""); setPreviewing(true);
    try {
      const [pv, sl, cf] = await Promise.all([
        publicApi.get("/auditions/public/preview", { params: { code: c } }),
        publicApi.get("/auditions/public/slots", { params: { code: c } }).catch(() => ({ data: [] })),
        publicApi.get("/auditions/public/fields", { params: { code: c } }).catch(() => ({ data: [] })),
      ]);
      setPreview(pv.data);
      setSlots(sl.data || []);
      setCustomFields(cf.data || []);
      setStep(2);
    } catch (e) {
      setPreviewErr(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
      setPreview(null);
    } finally { setPreviewing(false); }
  };

  const addConflict = () =>
    setForm((f) => ({ ...f, conflicts: [...f.conflicts, { date: "", start_time: "", end_time: "", reason: "" }] }));
  const setConflict = (i, patch) =>
    setForm((f) => ({ ...f, conflicts: f.conflicts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const rmConflict = (i) =>
    setForm((f) => ({ ...f, conflicts: f.conflicts.filter((_, idx) => idx !== i) }));

  const toggleRole = (name) => {
    setForm((f) => {
      const has = f.interested_roles.includes(name);
      return { ...f, interested_roles: has ? f.interested_roles.filter((r) => r !== name) : [...f.interested_roles, name] };
    });
  };
  const addCustomRole = () => {
    const v = roleQuery.trim();
    if (!v) return;
    if (!form.interested_roles.includes(v))
      setForm((f) => ({ ...f, interested_roles: [...f.interested_roles, v] }));
    setRoleQuery("");
  };

  const pickHeadshot = (file) => {
    setHeadshot(file);
    if (headshotPreviewUrl) URL.revokeObjectURL(headshotPreviewUrl);
    setHeadshotPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side guardrail — the backend enforces the same rules, but we
    // catch it here to give the auditioner an immediate, friendly message
    // instead of a raw 400.
    if (isMinor) {
      if (!form.parent_name.trim() || !form.parent_email.trim()) {
        setError("Because you're under 18, we need a parent or guardian's name and email.");
        return;
      }
      if (!form.parent_consent) {
        setError("A parent or guardian must tick the consent box to continue.");
        return;
      }
    }
    setSubmitting(true); setError("");
    try {
      const payload = { code: code.toUpperCase().trim(), ...form, slot_id: form.slot_id || null, custom_answers: customAnswers, website: honeypot };
      const { data } = await publicApi.post("/auditions/public/signup", payload);
      if (headshot) {
        const fd = new FormData(); fd.append("file", headshot);
        try { await publicApi.post(`/auditions/public/${data.id}/headshot`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch { /* optional */ }
      }
      // Upload any file-type custom answers
      for (const [fid, file] of Object.entries(customFiles)) {
        if (!file) continue;
        try {
          const fd = new FormData(); fd.append("file", file);
          await publicApi.post(`/auditions/public/${data.id}/custom-file/${fid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { /* skip */ }
      }
      setDone({ productionTitle: data.production_title || preview.production.title });
    } catch (e) {
      const status = e.response?.status;
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit audition");
      if (status === 409) {
        // slot just filled — refresh slots list
        try {
          const sl = await publicApi.get("/auditions/public/slots", { params: { code: code.toUpperCase().trim() } });
          setSlots(sl.data || []);
          setForm((f) => ({ ...f, slot_id: "" }));
        } catch { /* ignore */ }
      }
    } finally { setSubmitting(false); }
  };

  // SUCCESS SCREEN
  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div className="max-w-lg w-full text-center animate-fade-up" data-testid="audition-success">
          <div className="mx-auto w-14 h-14 rounded-full border border-emerald-500/40 bg-emerald-500/10 grid place-items-center mb-6">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Submission received</div>
          <h1 className="mt-2 font-display text-4xl leading-tight">You&apos;re on the list.</h1>
          <p className="mt-4 text-zinc-400">
            Thanks for signing up for <span className="text-gold">{done.productionTitle}</span>. The production team will review your submission and be in touch about scheduling.
          </p>
          <Link to="/login" className="mt-8 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white">Return to StageCall <ArrowRight className="w-3 h-3" /></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      {/* Left hero */}
      <div className="relative hidden md:flex flex-col justify-between p-10 overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${STAGE_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative">
          <div className="flex items-center gap-3">
            {preview?.production?.theater_logo_url ? (
              <img src={preview.production.theater_logo_url} alt={preview.production.theater_name || "Theatre"} className="w-12 h-12 rounded-md object-contain bg-[#121212] border border-white/10 p-1" />
            ) : (
              <div className="w-10 h-10 rounded-md border border-white/15 grid place-items-center bg-[#121212]">
                <Sparkles className="w-4 h-4 text-gold" />
              </div>
            )}
            <div>
              <div className="font-display text-2xl tracking-tight">{preview?.production?.theater_name || "StageCall"}</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Auditions</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
            Ready for<br />
            <span className="text-gold">your close-up?</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base leading-relaxed">
            Fill in your details, share a headshot, and tell us which roles you&apos;re excited about. It only takes a few minutes.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-start justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-xl space-y-6 animate-fade-up py-6">
          <div className="md:hidden flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-gold" /><span className="font-display text-2xl">StageCall</span>
          </div>

          {step === 1 && (
            <form onSubmit={(e) => { e.preventDefault(); fetchPreview(code.toUpperCase().trim()); }} className="space-y-6" data-testid="audition-code-form">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Step 1 / 2</div>
                <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Enter your audition code.</h2>
                <p className="mt-2 text-sm text-zinc-400">Six characters from the production listing. Case doesn&apos;t matter.</p>
              </div>
              <div>
                <Label htmlFor="code" className="text-zinc-300 text-xs uppercase tracking-widest">Code</Label>
                <div className="mt-2 relative">
                  <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input id="code" data-testid="audition-code-input" value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength={8} required autoFocus
                    className="pl-9 bg-[#121212] border-white/10 h-12 font-mono tracking-[0.4em] text-lg uppercase focus:border-gold focus:ring-1 focus:ring-gold"
                    placeholder="ABC123" />
                </div>
              </div>
              {previewErr && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{previewErr}</div>}
              <button data-testid="audition-continue-btn" type="submit" disabled={previewing}
                style={goldBtn} className="w-full h-11 rounded-md font-semibold hover:brightness-110">
                {previewing ? "Checking…" : "Continue"}
              </button>
            </form>
          )}

          {step === 2 && preview && (
            <form onSubmit={submit} className="space-y-6" data-testid="audition-form">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Step 2 / 2</div>
                <h2 className="mt-2 font-display text-3xl tracking-tight font-medium">Tell us about yourself.</h2>
              </div>

              <div className="rounded-md border border-gold/30 bg-gold/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-gold">You&apos;re auditioning for</div>
                <div className="mt-1 flex items-center gap-2">
                  <Theater className="w-4 h-4 text-gold" />
                  <div className="font-display text-xl" data-testid="audition-production-title">{preview.production.title}</div>
                </div>
                {preview.production.tagline && <div className="mt-1 text-sm text-zinc-400">{preview.production.tagline}</div>}
                <div className="mt-2 text-xs text-zinc-500 flex flex-wrap gap-3">
                  {preview.production.venue && <span>{preview.production.venue}</span>}
                  {preview.production.director && <span>Dir. {preview.production.director}</span>}
                </div>
              </div>

              {/* Slot picker */}
              {slots.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Pick your audition time</div>
                    {form.slot_id && <button type="button" onClick={() => setForm({ ...form, slot_id: "" })} className="text-[11px] text-zinc-500 hover:text-white">Clear</button>}
                  </div>
                  <div className="rounded-md border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid="slot-picker">
                    {(() => {
                      const groups = {};
                      slots.forEach((s) => { (groups[s.date] ||= []).push(s); });
                      const dates = Object.keys(groups).sort();
                      if (dates.length === 0) return null;
                      return dates.map((d) => (
                        <div key={d} data-testid={`slot-day-${d}`}>
                          <div className="sticky top-0 z-10 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-400 bg-[#0A0A0A] border-b border-white/5 flex items-center justify-between">
                            <span>{fmtDate(d)}</span>
                            <span className="text-zinc-600">{groups[d].length} slot{groups[d].length === 1 ? "" : "s"}</span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-2">
                            {groups[d].map((s) => {
                              const full = !s.available;
                              const selected = form.slot_id === s.id;
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  disabled={full && !selected}
                                  onClick={() => setForm({ ...form, slot_id: selected ? "" : s.id })}
                                  data-testid={`slot-${s.id}`}
                                  className={`text-xs rounded border px-2 py-1.5 transition-colors
                                    ${selected ? "bg-gold text-black border-gold font-semibold" :
                                      full ? "border-white/5 text-zinc-600 line-through cursor-not-allowed" :
                                      "border-white/10 text-zinc-200 hover:border-gold/40 hover:bg-white/5"}`}
                                >
                                  {fmtTime(s.start_time)}
                                  {s.capacity > 1 && <span className={`text-[10px] block ${selected ? "" : "opacity-60"}`}>{s.capacity - s.filled} / {s.capacity} open</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center justify-between gap-2 flex-wrap">
                    <span>Optional — but picking a time now guarantees your spot.</span>
                    {(() => {
                      const dc = new Set(slots.map((s) => s.date)).size;
                      return dc > 1 ? (
                        <span className="text-zinc-400" data-testid="slot-day-count">
                          {dc} audition days · {slots.length} time{slots.length === 1 ? "" : "s"} in total
                        </span>
                      ) : null;
                    })()}
                  </div>
                </section>
              )}

              {/* Contact */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Full name *</Label><Input data-testid="aud-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                  <div><Label>Email *</Label><Input data-testid="aud-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                  <div><Label>Phone</Label><Input data-testid="aud-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold" /></div>
                </div>
              </section>

              {/* Headshot */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Headshot</div>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-md border border-white/10 bg-[#121212] overflow-hidden grid place-items-center">
                    {headshotPreviewUrl ? <img src={headshotPreviewUrl} alt="preview" className="w-full h-full object-cover" /> : <UserIcon className="w-8 h-8 text-zinc-600" />}
                  </div>
                  <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-white/15 text-sm text-zinc-300 hover:bg-white/5 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>{headshot ? "Change photo" : "Upload photo"}</span>
                    <input data-testid="aud-headshot" type="file" accept="image/*" className="hidden" onChange={(e) => pickHeadshot(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <div className="text-xs text-zinc-500">Optional. PNG / JPG / WEBP up to 8 MB.</div>
              </section>

              {/* Details */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date of birth</Label>
                    <Input
                      type="date"
                      data-testid="aud-dob"
                      value={form.dob}
                      onChange={(e) => setForm({ ...form, dob: e.target.value })}
                      max={new Date().toISOString().slice(0, 10)}
                      className="bg-[#121212] border-white/10 h-11 focus:border-gold focus:ring-1 focus:ring-gold"
                    />
                    <div className="text-[10px] text-zinc-500 mt-1">We only use this to confirm age &amp; comply with child-privacy law.</div>
                  </div>
                  <div><Label>Age</Label><Input data-testid="aud-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="bg-[#121212] border-white/10 h-11" placeholder="e.g. 28 or 25-35" /></div>
                  <div><Label>Height</Label><Input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className="bg-[#121212] border-white/10 h-11" placeholder="e.g. 5'8" /></div>
                  <div><Label>Vocal range</Label><Input value={form.vocal_range} onChange={(e) => setForm({ ...form, vocal_range: e.target.value })} className="bg-[#121212] border-white/10 h-11" placeholder="Alto / Tenor / etc." /></div>
                  <div className="col-span-2"><Label>Dance experience</Label><Input value={form.dance_experience} onChange={(e) => setForm({ ...form, dance_experience: e.target.value })} className="bg-[#121212] border-white/10 h-11" placeholder="None / Some / Advanced" /></div>
                </div>
                <div><Label>Previous experience</Label><Textarea rows={3} value={form.previous_experience} onChange={(e) => setForm({ ...form, previous_experience: e.target.value })} className="bg-[#121212] border-white/10" placeholder="Recent credits, training, workshops…" /></div>
                <div><Label>Special skills</Label><Input value={form.special_skills} onChange={(e) => setForm({ ...form, special_skills: e.target.value })} className="bg-[#121212] border-white/10 h-11" placeholder="Accents, instruments, stage combat…" /></div>
              </section>

              {/* Parent/Guardian — appears ONLY when DOB puts the auditioner under 18. */}
              {isMinor && (
                <section className="space-y-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-4" data-testid="parental-consent-section">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-amber-400" />
                    <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300">Parent / guardian consent required</div>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Because you&apos;re under 18, a parent or guardian must give permission before we can accept your audition. We store this only to protect your data and won&apos;t share it with anyone outside the production team.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Parent / guardian full name *</Label><Input data-testid="aud-parent-name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="bg-[#121212] border-white/10 h-11" /></div>
                    <div><Label>Parent / guardian email *</Label><Input type="email" data-testid="aud-parent-email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="bg-[#121212] border-white/10 h-11" /></div>
                    <div><Label>Parent / guardian phone</Label><Input data-testid="aud-parent-phone" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="bg-[#121212] border-white/10 h-11" /></div>
                  </div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      data-testid="aud-parent-consent"
                      checked={!!form.parent_consent}
                      onChange={(e) => setForm({ ...form, parent_consent: e.target.checked })}
                      className="mt-0.5 accent-amber-400 w-4 h-4"
                    />
                    <span className="text-zinc-200">
                      I am the parent or legal guardian of this applicant and I consent to their audition submission and to the storage of their information under the production&apos;s privacy policy.
                    </span>
                  </label>
                </section>
              )}

              {/* Availability */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Availability</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Available from</Label><DatePickerField testid="aud-avail-start" value={form.availability_start} onChange={(v) => setForm({ ...form, availability_start: v })} placeholder="Pick start date" /></div>
                  <div><Label>Available until</Label><DatePickerField testid="aud-avail-end" value={form.availability_end} onChange={(v) => setForm({ ...form, availability_end: v })} placeholder="Pick end date" /></div>
                </div>
              </section>

              {/* Conflicts */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Known conflicts</div>
                  <button type="button" onClick={addConflict} data-testid="add-conflict-btn" className="text-xs text-gold inline-flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add conflict</button>
                </div>
                {form.conflicts.length === 0 && <div className="text-xs text-zinc-500">Add dates you know you can&apos;t be at rehearsal.</div>}
                {form.conflicts.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4"><Label>Date</Label><DatePickerField size="sm" value={c.date} onChange={(v) => setConflict(i, { date: v })} placeholder="Date" /></div>
                    <div className="col-span-2"><Label>From</Label><TimeInput12 value={c.start_time} onChange={(v) => setConflict(i, { start_time: v })} /></div>
                    <div className="col-span-2"><Label>To</Label><TimeInput12 value={c.end_time} onChange={(v) => setConflict(i, { end_time: v })} /></div>
                    <div className="col-span-3"><Label>Reason</Label><Input value={c.reason} onChange={(e) => setConflict(i, { reason: e.target.value })} className="bg-[#121212] border-white/10 h-9" placeholder="Work, class…" /></div>
                    <button type="button" onClick={() => rmConflict(i)} className="col-span-1 h-9 rounded border border-white/10 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4 mx-auto" /></button>
                  </div>
                ))}
              </section>

              {/* Interested roles */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Roles I&apos;m interested in</div>
                {preview.characters?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {preview.characters.map((c) => {
                      const selected = form.interested_roles.includes(c.name);
                      return (
                        <button key={c.id} type="button" onClick={() => toggleRole(c.name)}
                          data-testid={`role-chip-${c.name}`}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors
                            ${selected ? "bg-gold text-black border-gold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
                          {c.name}{c.voice_type && <span className="opacity-70 ml-1">· {c.voice_type}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input value={roleQuery} onChange={(e) => setRoleQuery(e.target.value)} placeholder="Add other roles / ensemble" className="bg-[#121212] border-white/10 h-10 flex-1" />
                  <button type="button" onClick={addCustomRole} className="h-10 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm">Add</button>
                </div>
                {form.interested_roles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {form.interested_roles.map((r) => (
                      <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30">
                        {r}
                        <button type="button" onClick={() => toggleRole(r)} className="ml-1 text-gold/70 hover:text-gold">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </section>

              {/* Virtual audition */}
              <section className="space-y-3" data-testid="virtual-audition-section">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="virtual-audition-checkbox"
                    checked={!!form.is_virtual}
                    onChange={(e) => setForm({ ...form, is_virtual: e.target.checked })}
                    className="mt-0.5 accent-gold w-4 h-4"
                  />
                  <span>
                    <span className="text-white">I am submitting a virtual audition</span>
                    <span className="block text-[11px] text-zinc-500">Tick this if you can&apos;t attend in person. Paste unlisted video links below — YouTube, Vimeo, or a shared cloud folder all work.</span>
                  </span>
                </label>
                {form.is_virtual && (
                  <div className="grid grid-cols-1 gap-2 pl-6">
                    <div><Label>Acting video link</Label><Input data-testid="video-acting-url" value={form.video_acting_url} onChange={(e) => setForm({ ...form, video_acting_url: e.target.value })} className="bg-[#121212] border-white/10 h-10" placeholder="https://youtu.be/…" /></div>
                    <div><Label>Vocal video link</Label><Input data-testid="video-vocal-url" value={form.video_vocal_url} onChange={(e) => setForm({ ...form, video_vocal_url: e.target.value })} className="bg-[#121212] border-white/10 h-10" placeholder="https://youtu.be/…" /></div>
                    <div><Label>Dance video link</Label><Input data-testid="video-dance-url" value={form.video_dance_url} onChange={(e) => setForm({ ...form, video_dance_url: e.target.value })} className="bg-[#121212] border-white/10 h-10" placeholder="https://youtu.be/…" /></div>
                    <div className="text-[10px] text-zinc-600">All three are optional. Only fill the ones the show is casting for.</div>
                  </div>
                )}
              </section>

              {/* Emergency contact */}
              <section className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Emergency contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="bg-[#121212] border-white/10 h-11" /></div>
                  <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="bg-[#121212] border-white/10 h-11" /></div>
                </div>
              </section>

              {/* Custom questions */}
              {customFields.length > 0 && (
                <section className="space-y-3" data-testid="custom-fields-section">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">More questions</div>
                  <div className="space-y-4">
                    {customFields.map((f) => (
                      <CustomFieldRenderer key={f.id} field={f}
                        value={f.type === "file" ? customFiles[f.id] : customAnswers[f.id]}
                        onChange={(v) => {
                          if (f.type === "file") setCustomFiles((s) => ({ ...s, [f.id]: v }));
                          else setCustomAnswers((s) => ({ ...s, [f.id]: v }));
                        }} />
                    ))}
                  </div>
                </section>
              )}

              {/* Honeypot — invisible to humans, filled by bots */}
              <input type="text" name="website" tabIndex="-1" autoComplete="off"
                value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                className="hidden" aria-hidden="true" data-testid="honeypot" />

              {error && <div data-testid="aud-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}

              <div className="flex items-center gap-2 sticky bottom-0 py-3 bg-[#0A0A0A]/95 backdrop-blur -mx-6 px-6 border-t border-white/10">
                <button type="button" onClick={() => { setStep(1); setPreview(null); }} className="h-11 px-4 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5">Back</button>
                <button data-testid="aud-submit-btn" type="submit" disabled={submitting} style={goldBtn} className="flex-1 h-11 rounded-md font-semibold hover:brightness-110 inline-flex items-center justify-center gap-1.5">
                  {submitting ? "Submitting…" : (<>Submit audition <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
              <div className="text-[10px] text-zinc-600 text-center pt-1">
                By submitting you agree to our <Link to="/terms" className="underline hover:text-zinc-400">Terms</Link> &amp; <Link to="/privacy" className="underline hover:text-zinc-400">Privacy Policy</Link>.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
